import { useEffect, useRef } from 'react';
import { Flex, IconButton, Image, Modal, ModalContent, ModalOverlay, Text } from '@chakra-ui/react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

interface Props {
	images: string[];
	// Index of the open image, or null when closed
	index: number | null;
	onChange: (index: number) => void;
	onClose: () => void;
}

// Minimum horizontal swipe distance (px) to change images on touch screens
const SWIPE_THRESHOLD = 50;

const controlStyles = {
	color: "white.100",
	backgroundColor: "blackAlpha.600",
	borderRadius: "full",
	_hover: { backgroundColor: "blackAlpha.800" },
	_focusVisible: { boxShadow: "0 0 0 3px #D0A144", outline: "none" },
};

/**
 * Full-screen viewer for case images with previous/next, arrow keys, and swipe
 */
export default function CaseLightbox({ images, index, onChange, onClose }: Props) {

	const isOpen = index !== null;
	const touchStartX = useRef<number | null>(null);

	// Wraps around at either end
	const showPrevious = () => index !== null && onChange((index - 1 + images.length) % images.length);
	const showNext = () => index !== null && onChange((index + 1) % images.length);

	// Arrow key navigation (Esc is handled by the modal)
	useEffect(() => {
		if (!isOpen) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "ArrowLeft") showPrevious();
			if (e.key === "ArrowRight") showNext();
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	});

	return (
		<Modal isOpen={isOpen} onClose={onClose} size={"full"}>
			<ModalOverlay backgroundColor={"blackAlpha.800"} />
			<ModalContent
				backgroundColor={"transparent"}
				boxShadow={"none"}
				// Clicking the empty space around the image closes the viewer
				onClick={onClose}
				onTouchStart={(e) => {
					touchStartX.current = e.touches[0].clientX;
				}}
				onTouchEnd={(e) => {
					if (touchStartX.current === null) return;
					const distance = e.changedTouches[0].clientX - touchStartX.current;
					touchStartX.current = null;

					if (distance > SWIPE_THRESHOLD) showPrevious();
					if (distance < -SWIPE_THRESHOLD) showNext();
				}}
			>
				{index !== null && (
					<Flex
						height={"100vh"}
						alignItems={"center"}
						justifyContent={"center"}
						paddingX={{ base: 4, md: 24 }}
						paddingY={16}
					>
						<Image
							src={images[index]}
							alt={`Past case ${index + 1}`}
							maxHeight={"100%"}
							maxWidth={"100%"}
							objectFit={"contain"}
							borderRadius={"md"}
							boxShadow={"dark-lg"}
							onClick={(e) => e.stopPropagation()}
						/>
					</Flex>
				)}

				{/* Close */}
				<IconButton
					aria-label={"Close"}
					icon={<X />}
					position={"absolute"}
					top={4}
					right={4}
					onClick={(e) => {
						e.stopPropagation();
						onClose();
					}}
					{...controlStyles}
				/>

				{/* Previous / Next */}
				{images.length > 1 && (
					<>
						<IconButton
							aria-label={"Previous case"}
							icon={<ChevronLeft size={28} />}
							position={"absolute"}
							left={{ base: 2, md: 6 }}
							top={"50%"}
							transform={"translateY(-50%)"}
							size={"lg"}
							onClick={(e) => {
								e.stopPropagation();
								showPrevious();
							}}
							{...controlStyles}
						/>
						<IconButton
							aria-label={"Next case"}
							icon={<ChevronRight size={28} />}
							position={"absolute"}
							right={{ base: 2, md: 6 }}
							top={"50%"}
							transform={"translateY(-50%)"}
							size={"lg"}
							onClick={(e) => {
								e.stopPropagation();
								showNext();
							}}
							{...controlStyles}
						/>
					</>
				)}

				{/* Counter */}
				{index !== null && (
					<Text
						position={"absolute"}
						bottom={4}
						left={"50%"}
						transform={"translateX(-50%)"}
						paddingX={3}
						paddingY={1}
						borderRadius={"full"}
						backgroundColor={"blackAlpha.600"}
						color={"white.100"}
						fontFamily={"Montserrat"}
						fontWeight={"600"}
						fontSize={"sm"}
					>
						{index + 1} / {images.length}
					</Text>
				)}
			</ModalContent>
		</Modal>
	);
}
